import React, { useState, useRef, useEffect } from 'react'
import { TimeEntry, Project, CreateTimeEntryRequest, UpdateTimeEntryRequest } from '../../../shared/types'
import CustomDropdown from './CustomDropdown'
import DateRangePicker from './DateRangePicker'
import CustomScrollbar from './CustomScrollbar'
import { format, differenceInSeconds } from 'date-fns'
import { getEffectiveTimezone, getBrowserTimezone, getTimezoneOffsetInfo } from '@/utils/timezoneOptions'

interface TimeEntryFormProps {
  mode: 'create' | 'edit'
  entry?: TimeEntry // Required for edit mode
  projects: Project[]
  onSubmit: (data: CreateTimeEntryRequest | UpdateTimeEntryRequest) => Promise<void>
  onCancel: () => void
  loading: boolean
  initialStartTime?: Date // For pre-filling start time in create mode
  initialEndTime?: Date // For pre-filling end time in create mode
  preferredTimezone?: string | null // User's preferred timezone setting (null = use browser)
  weekStartsOn?: 0 | 1 // 0 = Sunday, 1 = Monday (default)
}

export default function TimeEntryForm({
  mode,
  entry,
  projects,
  onSubmit,
  onCancel,
  loading,
  initialStartTime,
  initialEndTime,
  preferredTimezone,
  weekStartsOn = 1
}: TimeEntryFormProps) {
  // Get the effective timezone to use for new entries
  const effectiveTimezone = getEffectiveTimezone(preferredTimezone);
  const browserTimezone = getBrowserTimezone();
  const isUsingNonBrowserTimezone = effectiveTimezone !== browserTimezone;


  // Extract time from ISO string (HH:MM format)
  const extractTimeFromISO = (isoString: string) => {
    const date = new Date(isoString)
    const hours = String(date.getHours()).padStart(2, '0')
    const minutes = String(date.getMinutes()).padStart(2, '0')
    return `${hours}:${minutes}`
  }

  // Initialize state based on mode
  const [description, setDescription] = useState(mode === 'edit' && entry ? entry.description : '')
  const [selectedProjectId, setSelectedProjectId] = useState(mode === 'edit' && entry ? entry.projectId || '' : '')
  const [selectedLocation, setSelectedLocation] = useState(mode === 'edit' && entry ? entry.location || '' : '')

  // Helper to get initial date/time values
  const getInitialStartDate = () => {
    if (mode === 'edit' && entry) return new Date(entry.startTime)
    if (initialStartTime) return initialStartTime
    return new Date()
  }

  const getInitialStartTime = () => {
    if (mode === 'edit' && entry) return extractTimeFromISO(entry.startTime)
    if (initialStartTime) return extractTimeFromISO(initialStartTime.toISOString())
    return extractTimeFromISO(new Date().toISOString())
  }

  const getInitialEndDate = () => {
    if (mode === 'edit' && entry && entry.endTime) return new Date(entry.endTime)
    if (initialEndTime) return initialEndTime
    return new Date()
  }

  const getInitialEndTime = () => {
    if (mode === 'edit' && entry && entry.endTime) return extractTimeFromISO(entry.endTime)
    if (initialEndTime) return extractTimeFromISO(initialEndTime.toISOString())
    return extractTimeFromISO(new Date().toISOString())
  }

  // Date and time state
  const [startDate, setStartDate] = useState(getInitialStartDate)
  const [startTime, setStartTime] = useState(getInitialStartTime)
  const [endDate, setEndDate] = useState(getInitialEndDate)
  const [endTime, setEndTime] = useState(getInitialEndTime)
  
  const [errors, setErrors] = useState<{ description?: string; location?: string }>({})
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const [currentTime, setCurrentTime] = useState(new Date())

  // Update current time every second for running entries
  useEffect(() => {
    if (mode === 'edit' && entry && !entry.endTime) {
      const interval = setInterval(() => {
        setCurrentTime(new Date())
      }, 1000)

      return () => clearInterval(interval)
    }
  }, [mode, entry])

  // Handle ESC key to close the form
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCancel()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [onCancel])

  // Calculate duration in seconds
  const calculateDuration = () => {
    const createDateTimeString = (date: Date, time: string) => {
      const [hours, minutes] = time.split(':').map(Number)
      const combinedDate = new Date(date)
      combinedDate.setHours(hours, minutes, 0, 0)
      return combinedDate
    }

    const startDateTime = createDateTimeString(startDate, startTime)
    
    // For running entries, use current time as end time
    if (mode === 'edit' && entry && !entry.endTime) {
      return differenceInSeconds(currentTime, startDateTime)
    }
    
    const endDateTime = createDateTimeString(endDate, endTime)
    return differenceInSeconds(endDateTime, startDateTime)
  }

  // Format duration for display
  const formatDurationDisplay = (seconds: number) => {
    if (seconds < 0) return 'Invalid time range'
    
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`
    }
    return `${minutes}m`
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    // Clear previous errors
    setErrors({})
    
    const newErrors: { description?: string; location?: string } = {}
    
    // Validation
    if (mode === 'create' && (!description.trim() || !startDate)) {
      newErrors.description = 'Please enter a description and start time'
    } else if (!description.trim()) {
      newErrors.description = 'Please enter a description'
    }

    // Check if selected project has locations and location is required
    const selectedProject = projects.find(p => p.id === selectedProjectId)
    if (selectedProject && selectedProject.locations.length > 0 && !selectedLocation) {
      newErrors.location = 'Please select a location for this project'
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    // Create combined date-time strings
    // In edit mode, preserve original seconds/milliseconds to maintain data consistency
    // This prevents false overlap detection when entries are sequentially adjacent
    const createDateTimeString = (date: Date, time: string, originalTimestamp?: string) => {
      const [hours, minutes] = time.split(':').map(Number)
      const combinedDate = new Date(date)

      // In edit mode, preserve original seconds and milliseconds if the HH:MM hasn't changed
      // This prevents losing precision when user only changes other fields
      if (mode === 'edit' && originalTimestamp) {
        const original = new Date(originalTimestamp)
        const originalHours = original.getHours()
        const originalMinutes = original.getMinutes()

        // If hours and minutes match the original, preserve full precision
        if (hours === originalHours && minutes === originalMinutes) {
          combinedDate.setHours(hours, minutes, original.getSeconds(), original.getMilliseconds())
        } else {
          // User changed the time, use 0 seconds
          combinedDate.setHours(hours, minutes, 0, 0)
        }
      } else {
        // Create mode: use 0 seconds (expected since user inputs HH:MM only)
        combinedDate.setHours(hours, minutes, 0, 0)
      }

      return combinedDate.toISOString()
    }

    // Prepare data based on mode
    if (mode === 'create') {
      const createData: CreateTimeEntryRequest = {
        description: description.trim(),
        projectId: selectedProjectId || undefined,
        startTime: createDateTimeString(startDate, startTime),
        endTime: endDate ? createDateTimeString(endDate, endTime) : undefined,
        location: selectedLocation || undefined,
        // Include timezone when creating new entries - use preferred timezone if set
        timezone: effectiveTimezone,
      }
      await onSubmit(createData)
    } else {
      // When editing, do NOT include timezone - preserve the original
      // Pass original timestamps to preserve seconds/milliseconds if HH:MM unchanged
      const updateData: UpdateTimeEntryRequest = {
        description: description.trim(),
        projectId: selectedProjectId || undefined,
        startTime: createDateTimeString(startDate, startTime, entry?.startTime),
        endTime: entry?.endTime ? (endDate ? createDateTimeString(endDate, endTime, entry?.endTime) : undefined) : undefined,
        location: selectedLocation || undefined,
      }
      await onSubmit(updateData)
    }
  }



  const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value
    setDescription(value)
    
    // Auto-resize the textarea
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
      const scrollHeight = textareaRef.current.scrollHeight
      const lineHeight = 20 // Approximate line height in pixels
      const maxHeight = lineHeight * 50 // Maximum 50 lines
      const newHeight = Math.min(scrollHeight, maxHeight)
      textareaRef.current.style.height = `${newHeight}px`
    }
  }

  // Effect to handle initial resize
  useEffect(() => {
    if (textareaRef.current && description) {
      textareaRef.current.style.height = 'auto'
      const scrollHeight = textareaRef.current.scrollHeight
      const lineHeight = 20
      const maxHeight = lineHeight * 50
      const newHeight = Math.min(scrollHeight, maxHeight)
      textareaRef.current.style.height = `${newHeight}px`
    }
  }, [description])



  // Default timezone for legacy entries that don't have timezone stored
  const DEFAULT_TIMEZONE = 'Europe/Berlin'

  // Render timezone box for entries recorded in a different timezone
  const renderTimezoneBox = () => {
    // Format timezone for display (e.g., "America/New_York" -> "America/New York")
    const formatTimezone = (tz: string) => tz.replace(/_/g, ' ')

    // For create mode: show if using non-browser timezone
    // For edit mode: show if entry was recorded in a different timezone than current browser
    // Legacy entries without timezone default to Europe/Berlin
    let timezoneToShow: string | null = null

    if (mode === 'create' && isUsingNonBrowserTimezone) {
      timezoneToShow = effectiveTimezone
    } else if (mode === 'edit') {
      const entryTimezone = entry?.timezone || DEFAULT_TIMEZONE
      if (entryTimezone !== browserTimezone) {
        timezoneToShow = entryTimezone
      }
    }

    if (!timezoneToShow) return null

    // Get timezone offset info for direction and formatted difference
    const offsetInfo = getTimezoneOffsetInfo(timezoneToShow, browserTimezone)

    // Build display text with timezone and time difference
    const timezoneDisplay = offsetInfo.direction === 'same'
      ? formatTimezone(timezoneToShow)
      : `${formatTimezone(timezoneToShow)} (${offsetInfo.formattedDifference})`

    // Use gray color for same time offset (different timezone but no time difference)
    const iconColorClass = offsetInfo.direction === 'same' ? 'text-gray-400' : 'text-blue-600'

    return (
      <div className="bg-gradient-to-br from-gray-50/80 via-blue-50/20 to-indigo-50/10 backdrop-blur-sm rounded-lg p-4 mb-4 border border-gray-200/70 shadow-sm">
        <div className="flex items-center space-x-2">
          <span className={`inline-flex items-center justify-center w-4 h-4 ${iconColorClass}`}>
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              {/* Clock circle */}
              <circle cx="12" cy="12" r="8" />
              {/* Clock hands */}
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 8v4l2.5 2.5"
              />
              {offsetInfo.direction === 'behind' ? (
                // Counter-clockwise arrow (timezone is behind browser = earlier time)
                <>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v4h4" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 8A9 9 0 0112 3" />
                </>
              ) : offsetInfo.direction === 'ahead' ? (
                // Clockwise arrow (timezone is ahead of browser = later time)
                <>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M20 4v4h-4" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M20 8A9 9 0 0012 3" />
                </>
              ) : null /* 'same' direction - no arrow, just clock */}
            </svg>
          </span>
          <span className="text-sm text-gray-700">
            Timezone: <span className="font-medium text-gray-900">{timezoneDisplay}</span>
          </span>
        </div>
      </div>
    )
  }

  // Render time range display
  const renderTimeRange = () => {
    const duration = calculateDuration()

    return (
      <div className="bg-gradient-to-br from-gray-50/80 via-blue-50/20 to-indigo-50/10 backdrop-blur-sm rounded-lg p-4 mb-4 border border-gray-200/70 shadow-sm">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-medium text-gray-700">Entry Time Range</h3>
          <span className="text-sm font-semibold text-blue-600">
            {formatDurationDisplay(duration)}
          </span>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="text-xs text-gray-500 mb-1">Start</div>
            <div className="text-sm font-medium text-gray-900">
              {format(startDate, 'MMM d, yyyy')} at {startTime}
            </div>
          </div>

          {(mode === 'create' || (mode === 'edit' && entry?.endTime)) ? (
            <div>
              <div className="text-xs text-gray-500 mb-1">End</div>
              <div className="text-sm font-medium text-gray-900">
                {format(endDate, 'MMM d, yyyy')} at {endTime}
              </div>
            </div>
          ) : (
            <div>
              <div className="text-xs text-gray-500 mb-1">Status</div>
              <div className="text-sm font-medium text-orange-600">
                Running
              </div>
            </div>
          )}
        </div>

        {duration < 0 && (
          <div className="mt-2 text-xs text-red-600">
            End time must be after start time
          </div>
        )}
      </div>
    )
  }

  return (
    <div 
      className="fixed inset-0 bg-gradient-to-br from-black/20 via-blue-900/5 to-indigo-900/8 backdrop-blur-sm flex items-center justify-center p-4 z-50 overflow-y-auto"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onCancel();
        }
      }}
    >
      <CustomScrollbar
        className="bg-gradient-to-br from-white/95 via-blue-50/25 to-indigo-50/35 backdrop-blur-sm border border-gray-200/80 shadow-xl max-w-md w-full p-6 my-8 max-h-[90vh] overflow-y-auto"
        style={{
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          borderImage: 'linear-gradient(135deg, rgba(59, 130, 246, 0.08), rgba(99, 102, 241, 0.08)) 1'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Time Range Display */}
        {renderTimeRange()}

        {/* Timezone Display */}
        {renderTimezoneBox()}
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
              Description *
            </label>
            <textarea
              ref={textareaRef}
              id="description"
              value={description}
              onChange={handleDescriptionChange}
              className={`w-full px-3 py-2 border border-gray-300/70 bg-white/95 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/60 resize-none overflow-hidden transition-all duration-200 text-sm ${
                errors.description ? 'border-red-500' : ''
              }`}
              placeholder="Enter description"
              rows={1}
              maxLength={500}
            />
            {errors.description && (
              <p className="mt-1 text-sm text-red-600">{errors.description}</p>
            )}
          </div>

          <CustomDropdown
            id={`${mode}-project`}
            label="Project"
            placeholder="Select a project"
            options={projects.map(project => ({ value: project.id, label: project.name }))}
            selectedValues={selectedProjectId ? [selectedProjectId] : []}
            onSelectionChange={(values) => {
              const newProjectId = values[0] || ''
              setSelectedProjectId(newProjectId)
              
              if (!newProjectId) {
                // Clear location if project is cleared
                setSelectedLocation('')
              } else {
                // Auto-select first location if project has locations
                const selectedProject = projects.find(p => p.id === newProjectId)
                if (selectedProject && selectedProject.locations.length > 0) {
                  setSelectedLocation(selectedProject.locations[0].name)
                } else {
                  setSelectedLocation('')
                }
              }
            }}
            multiSelect={false}
            rounded={false}
            searchable={true}
            searchPlaceholder="Search projects..."
            minWidth="min-w-[250px]"
          />

          <CustomDropdown
            id={`${mode}-location`}
            label={(() => {
              const selectedProject = projects.find(p => p.id === selectedProjectId)
              const hasLocations = selectedProject && selectedProject.locations.length > 0
              return hasLocations ? "Location *" : "Location"
            })()}
            placeholder={(() => {
              if (!selectedProjectId) return "Select a project first"
              const selectedProject = projects.find(p => p.id === selectedProjectId)
              if (!selectedProject || selectedProject.locations.length === 0) return "No locations available"
              return "Select a location"
            })()}
            options={(() => {
              const selectedProject = projects.find(p => p.id === selectedProjectId)
              if (!selectedProject) return []
              return selectedProject.locations.map((location: { name: string }) => ({ value: location.name, label: location.name }))
            })()}
            selectedValues={selectedLocation ? [selectedLocation] : []}
            onSelectionChange={(values) => setSelectedLocation(values[0] || '')}
            multiSelect={false}
            disabled={!selectedProjectId}
            rounded={false}
          />
          {errors.location && (
            <p className="mt-1 text-sm text-red-600">{errors.location}</p>
          )}

          <DateRangePicker
            id={`${mode}-time-range`}
            label="Time Range *"
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
            startTime={startTime}
            endTime={endTime}
            onStartTimeChange={setStartTime}
            onEndTimeChange={setEndTime}
            minWidth="min-w-[320px]"
            rounded={false}
            weekStartsOn={weekStartsOn}
            transparency="none"
          />

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 px-4 py-2 border border-gray-300/70 text-gray-700 bg-white/95 backdrop-blur-sm hover:bg-gray-50/90 focus:outline-none focus:ring-2 focus:ring-gray-500/50 transition-all duration-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
            >
              {loading ? (mode === 'create' ? 'Creating...' : 'Updating...') : (mode === 'create' ? 'Create Entry' : 'Update Entry')}
            </button>
          </div>
        </form>
      </CustomScrollbar>
    </div>
  )
} 